import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface CompanyProfile {
  name: string;
  representativeLastName: string;
  representativeFirstName: string;
  postalCode: string;
  address: string;
  phone: string;
}

interface StoreProfile {
  name: string;
  managerLastName: string;
  managerFirstName: string;
  postalCode: string;
  address: string;
  phone: string;
}

interface ProfileResponse {
  company: CompanyProfile;
  store: StoreProfile;
}

// createApiRequest はHTTPエラー時にレスポンス本文(JSON文字列)を Error.message に入れる。
// BE は {status,message,timestamp} を返すため、message を取り出して表示する。
const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

// 日本の郵便番号: 7桁 または 3桁-4桁。
const POSTAL_RE = /^[0-9]{7}$|^[0-9]{3}-[0-9]{4}$/;
// 日本の電話番号。国内(先頭0・合計10〜11桁) または 国際(+81 + 先頭0を除いた先頭非0の9〜10桁)。
// ハイフンは桁間に単独で挿入可。例: 03-1234-5678 / 09012345678 / +81-90-1234-5678。
const PHONE_RE = /^(?:0(?:\d-?){8,9}\d|\+81-?[1-9](?:-?\d){8,9})$/;

/** 表示モードの 1 行（ラベル＋値）。 */
const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.75, gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
      {value || '—'}
    </Typography>
  </Box>
);

/**
 * 企業・店舗情報セクション。要件定義 0001 §6 / feast-fe ADR 0010。
 * BE: GET/PUT /stores/{storeId}/profile。
 * 初期状態は表示（読み取り専用）。管理者が「編集」を押したときだけ編集モードに切り替わり、保存/キャンセルできる。
 * 郵便番号・電話は日本の規格（国内/+81）の単一入力欄で扱う。最終強制は BE。
 */
const StoreProfileSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';

  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [store, setStore] = useState<StoreProfile | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchProfile = useCallback(() => {
    if (!store_id) return;
    setLoading(true);
    createApiRequest<ProfileResponse>(`/stores/${store_id}/profile`, { method: 'GET' })
      .then((data) => {
        setCompany(data.company);
        setStore(data.store);
      })
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setLoading(false));
  }, [store_id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const setCompanyField = (key: keyof CompanyProfile) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompany((prev) => (prev ? { ...prev, [key]: e.target.value } : prev));
  };
  const setStoreField = (key: keyof StoreProfile) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setStore((prev) => (prev ? { ...prev, [key]: e.target.value } : prev));
  };

  const startEdit = () => {
    setError(null);
    setSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    // 編集内容を破棄し、サーバの値へ戻す。
    setError(null);
    setEditing(false);
    fetchProfile();
  };

  const validate = (c: CompanyProfile, s: StoreProfile): string | null => {
    if (
      !c.name.trim() || !c.representativeLastName.trim() || !c.representativeFirstName.trim() ||
      !c.address.trim() || !s.name.trim() || !s.managerLastName.trim() || !s.managerFirstName.trim() || !s.address.trim()
    ) {
      return 'すべての項目を入力してください。';
    }
    if (!POSTAL_RE.test(c.postalCode.trim()) || !POSTAL_RE.test(s.postalCode.trim())) {
      return '郵便番号の形式が正しくありません（例: 1500001 または 150-0001）。';
    }
    if (!PHONE_RE.test(c.phone.trim()) || !PHONE_RE.test(s.phone.trim())) {
      return '電話番号の形式が正しくありません（例: 03-1234-5678 / 09012345678 / +81-90-1234-5678）。';
    }
    return null;
  };

  const save = () => {
    if (!store_id || !company || !store) return;
    const v = validate(company, store);
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSuccess(false);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ company, store }),
    })
      .then(() => {
        setSuccess(true);
        setEditing(false);
        fetchProfile();
      })
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setBusy(false));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company || !store) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">企業・店舗情報</Typography>
        {/* 編集は管理者のみ。必要なときだけ編集モードに入る。 */}
        {isManager && !editing && (
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={startEdit}>
            編集
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && !editing && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          保存しました。
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2 }}>
        {editing ? (
          <>
            <Typography variant="subtitle2" gutterBottom>企業情報</Typography>
            <TextField label="企業名" value={company.name} onChange={setCompanyField('name')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField label="代表者 氏（姓）" value={company.representativeLastName} onChange={setCompanyField('representativeLastName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
              <TextField label="代表者 名" value={company.representativeFirstName} onChange={setCompanyField('representativeFirstName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
            </Box>
            <TextField label="郵便番号（例: 150-0001）" value={company.postalCode} onChange={setCompanyField('postalCode')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 8 }} />
            <TextField label="企業住所" value={company.address} onChange={setCompanyField('address')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 255 }} />
            <TextField label="電話番号（例: 03-1234-5678 / +81-90-1234-5678）" value={company.phone} onChange={setCompanyField('phone')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 20 }} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>店舗情報</Typography>
            <TextField label="店舗名" value={store.name} onChange={setStoreField('name')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField label="店舗責任者 氏（姓）" value={store.managerLastName} onChange={setStoreField('managerLastName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
              <TextField label="店舗責任者 名" value={store.managerFirstName} onChange={setStoreField('managerFirstName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
            </Box>
            <TextField label="郵便番号（例: 150-0001）" value={store.postalCode} onChange={setStoreField('postalCode')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 8 }} />
            <TextField label="店舗住所" value={store.address} onChange={setStoreField('address')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 255 }} />
            <TextField label="電話番号（例: 03-1234-5678 / +81-90-1234-5678）" value={store.phone} onChange={setStoreField('phone')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 20 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              ※ 店舗の電話番号は、予約確認などお客様・スタッフ向けに送信するメールに記載されます。
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
              <Button variant="text" color="inherit" onClick={cancelEdit} disabled={busy}>
                キャンセル
              </Button>
              <Button variant="contained" onClick={save} disabled={busy}>
                保存
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>企業情報</Typography>
            <Row label="企業名" value={company.name} />
            <Row label="代表者" value={`${company.representativeLastName} ${company.representativeFirstName}`} />
            <Row label="郵便番号" value={company.postalCode} />
            <Row label="住所" value={company.address} />
            <Row label="電話番号" value={company.phone} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>店舗情報</Typography>
            <Row label="店舗名" value={store.name} />
            <Row label="店舗責任者" value={`${store.managerLastName} ${store.managerFirstName}`} />
            <Row label="郵便番号" value={store.postalCode} />
            <Row label="住所" value={store.address} />
            <Row label="電話番号" value={store.phone} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default StoreProfileSection;
